'use client';

import { useState } from 'react';
import Link from 'next/link';
import { api } from '@/lib/api/client';
import type { FinderConfig, FinderResult } from '@/lib/api/types';
import { ScoreBadge } from './ScoreBadge';

export function FinderWizard({ config }: { config: FinderConfig }) {
  const [answers, setAnswers] = useState<Record<number, unknown>>({});
  const [result, setResult] = useState<FinderResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setAnswer(questionId: number, value: unknown) {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.finder.submit(config.category.slug, answers);
      setResult(data);
    } catch {
      setError('No se pudo calcular la recomendación. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  }

  if (result) {
    return (
      <div>
        <h2 className="text-xl font-semibold text-navy">Tus recomendaciones</h2>
        <div className="mt-6 flex flex-col gap-4">
          {result.recommendations.map((rec) => (
            <div
              key={rec.product.slug}
              className="flex flex-col gap-3 rounded-lg border border-gray-border p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-navy text-xs font-semibold text-white">
                  {rec.rank}
                </span>
                <div>
                  <Link
                    href={`/productos/${rec.product.slug}`}
                    className="font-medium text-navy hover:text-cobalt"
                  >
                    {rec.product.short_title ?? rec.product.title}
                  </Link>
                  <p className="mt-1 text-xs text-gray-neutral">{rec.explanation}</p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3 self-end sm:self-auto">
                <div className="text-right">
                  <div className="text-xs text-gray-neutral">Compatibilidad</div>
                  <div className="text-sm font-semibold text-cobalt">
                    {Math.round(rec.suitability_score)}%
                  </div>
                </div>
                {rec.product.score !== null && <ScoreBadge score={rec.product.score} />}
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setResult(null)}
          className="mt-6 text-sm font-medium text-cobalt hover:underline"
        >
          Volver a empezar
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-8">
      {config.questions.map((question) => (
        <div key={question.id}>
          <label className="block text-sm font-medium text-navy">{question.question_text}</label>
          {question.help_text && (
            <p className="mt-1 text-xs text-gray-neutral">{question.help_text}</p>
          )}

          <div className="mt-3">
            {question.type === 'single_choice' && (
              <div className="flex flex-wrap gap-2">
                {question.options.map((option) => (
                  <label
                    key={option.value}
                    className={`cursor-pointer rounded-full border px-4 py-2 text-sm ${
                      answers[question.id] === option.value
                        ? 'border-cobalt bg-cobalt/10 text-cobalt'
                        : 'border-gray-border text-navy'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`q-${question.id}`}
                      value={option.value}
                      className="sr-only"
                      onChange={() => setAnswer(question.id, option.value)}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            )}

            {question.type === 'multi_choice' && (
              <div className="flex flex-wrap gap-2">
                {question.options.map((option) => {
                  const selected = Array.isArray(answers[question.id])
                    ? (answers[question.id] as string[])
                    : [];
                  const checked = selected.includes(option.value);

                  return (
                    <label
                      key={option.value}
                      className={`cursor-pointer rounded-full border px-4 py-2 text-sm ${
                        checked ? 'border-cobalt bg-cobalt/10 text-cobalt' : 'border-gray-border text-navy'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        onChange={() =>
                          setAnswer(
                            question.id,
                            checked
                              ? selected.filter((v) => v !== option.value)
                              : [...selected, option.value]
                          )
                        }
                      />
                      {option.label}
                    </label>
                  );
                })}
              </div>
            )}

            {question.type === 'boolean' && (
              <div className="flex gap-2">
                {[
                  { label: 'Sí', value: true },
                  { label: 'No', value: false },
                ].map((opt) => (
                  <label
                    key={opt.label}
                    className={`cursor-pointer rounded-full border px-4 py-2 text-sm ${
                      answers[question.id] === opt.value
                        ? 'border-cobalt bg-cobalt/10 text-cobalt'
                        : 'border-gray-border text-navy'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`q-${question.id}`}
                      className="sr-only"
                      onChange={() => setAnswer(question.id, opt.value)}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}

            {question.type === 'numeric' && (
              <input
                type="number"
                className="w-full max-w-xs rounded border border-gray-border px-3 py-2 text-sm"
                onChange={(e) => setAnswer(question.id, Number(e.target.value))}
              />
            )}

            {question.type === 'range' && (
              <input
                type="number"
                placeholder="Máximo"
                className="w-full max-w-xs rounded border border-gray-border px-3 py-2 text-sm"
                onChange={(e) => setAnswer(question.id, { max: Number(e.target.value) })}
              />
            )}
          </div>
        </div>
      ))}

      {error && <p className="text-sm text-rose-600">{error}</p>}

      <button
        type="submit"
        disabled={loading}
        className="w-full rounded bg-cobalt py-3 text-center text-sm font-semibold text-white hover:bg-cobalt-dark disabled:opacity-60 sm:w-auto sm:px-8"
      >
        {loading ? 'Calculando…' : 'Ver mis recomendaciones'}
      </button>
    </form>
  );
}
